import React, { Suspense, useEffect } from 'react';
import { lazyWithRetry } from './lib/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/layout/ScrollToTop';
import CookieConsent from './components/CookieConsent';
import Chatbot from './components/chat/Chatbot';
import SeoOverride from './components/SeoOverride';
import MaintenanceChecker from './components/MaintenanceChecker';
import ErrorBoundary from './components/ErrorBoundary';
import HeaderModern from './components/layout/HeaderModern';
import FooterModern from './components/layout/FooterModern';
import BottomNav from './components/layout/BottomNav';
import Home from './pages/Home';
import Maintenance from './pages/Maintenance';
import Training from './pages/Training';
import SurLeTerrain from './pages/SurLeTerrain';
// Pages refondues (chargées en lazy → code-splitting, comme la prod)
const About = lazyWithRetry(() => import('./pages/About'));
const Products = lazyWithRetry(() => import('./pages/Products'));
const Services = lazyWithRetry(() => import('./pages/Services'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Invest = lazyWithRetry(() => import('./pages/Invest'));
const ProxyPower = lazyWithRetry(() => import('./pages/ProxyPower'));
const Careers = lazyWithRetry(() => import('./pages/Careers'));
const Stations = lazyWithRetry(() => import('./pages/Stations'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const LegalPage = lazyWithRetry(() => import('./pages/LegalPage'));
const PayAsYouGo = lazyWithRetry(() => import('./pages/PayAsYouGo'));
const Resources = lazyWithRetry(() => import('./pages/Resources'));
const Faq = lazyWithRetry(() => import('./pages/Faq'));
const Blog = lazyWithRetry(() => import('./pages/Blog'));
const BlogArticle = lazyWithRetry(() => import('./pages/BlogArticle'));
const EmissionCalculator = lazyWithRetry(() => import('./pages/EmissionCalculator'));
const ProductDetail = lazyWithRetry(() => import('./pages/ProductDetail'));
const ProxyAcademy = lazyWithRetry(() => import('./pages/ProxyAcademy'));
const Flit = lazyWithRetry(() => import('./pages/Flit'));
const Newsroom = lazyWithRetry(() => import('./pages/Newsroom'));
const Press = lazyWithRetry(() => import('./pages/Press'));
const Events = lazyWithRetry(() => import('./pages/Events'));

// Admin embarqué retiré (P4) — l'administration est l'Operations Hub externe (source de vérité unique).
const ADMIN_HUB_URL = 'https://proxyemobility-admin.netlify.app';
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => { if (typeof window !== 'undefined') window.location.replace(to); }, [to]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[200] focus:rounded-lg focus:bg-[#18994E] focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white focus:shadow-lg"
          >
            Aller au contenu principal
          </a>
          <ScrollToTop />
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-200 border-t-[#18994E]" /></div>}>
          <Routes>
          {/* Maintenance Route - Always accessible */}
          <Route path="/maintenance" element={<Maintenance />} />

          {/* Admin → Operations Hub externe (source de vérité unique) */}
          <Route path="/admin/*" element={<ExternalRedirect to={ADMIN_HUB_URL} />} />
          
          {/* Public Routes - Wrapped with MaintenanceChecker */}
          <Route path="/" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Home />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/about" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <About />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/products" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Products />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/products/:slug" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <ProductDetail />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/services" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Services />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/training" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Training />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/blog" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Blog />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/blog/:slug" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <BlogArticle />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/contact" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Contact />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/faq" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Faq />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/careers" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Careers />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/stations" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Stations />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/sur-le-terrain" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <SurLeTerrain />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/resources" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Resources />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/pay-as-you-go" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <PayAsYouGo />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/proxy-power" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <ProxyPower />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/emission-calculator" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <EmissionCalculator />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/invest" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Invest />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/legal" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/privacy" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/terms" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/cookies" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          {/* Proxy Academy (refonte de /training) */}
          <Route path="/academy" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <ProxyAcademy />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/proxy-academy" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <ProxyAcademy />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          {/* Newsroom / Presse / Événements (refonte de Sur le terrain) */}
          <Route path="/newsroom" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Newsroom />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/press" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Press />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/events" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Events />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          {/* /flit → redirige vers /proxy-fleet */}
          <Route path="/flit" element={<Navigate to="/proxy-fleet" replace />} />
          <Route path="/proxy-fleet" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Flit />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="*" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <NotFound />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
        </Routes>
        </Suspense>
        <SeoOverride />
        <BottomNav />
        <CookieConsent />
        <Chatbot />
      </Router>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;